/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

import { Button } from "../../components/ui/button";
import { Trash2, Eye } from "lucide-react";
import VideoSquareCover from "../../video-square.jpg";
import AudioSquareCover from "../../audio-square.jpg";
import { getFileType } from "../../helper/function";
import { actions } from "../../actions";
import CustomLoader from "../ui/custom-loader";
import { services } from "../../services";
import { Web3Data } from "../../types/nft/create.rental";
import { useToast } from "../../components/ui/use-toast";
import { web3 } from "../../web3";
import {
  StyleProps,
  NFTRecord,
  ButtonProps,
  CustomRowLayoutProps,
} from "../../types/nft/rental";
import getContractAddresses from "../../contractData/contractAddress/addresses";
import { Web3Error } from "web3";
import { sendTransactionHash } from "../../services/transactionApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const useStyles = {
  imgContainer: {
    img: {
      height: "60px",
      width: "60px",
      objectFit: "contain" as const,
      margin: "10px",
    },
  },
};

const RentStatusField = ({ record }: { record: NFTRecord }) => (
  <span>{record.rentPaid ? "Paid" : "Unpaid"}</span>
);

const CustomRowLayout: React.FC<CustomRowLayoutProps> = ({ record }) => {
  const [ext, setExt] = useState<string | false>(false);

  useEffect(() => {
    async function getExtenstion(): Promise<void> {
      if (!record?.assetId?.image?.format) {
        const ext = await getFileType(record?.assetId?.image?.compressed);
        setExt(ext);
      } else {
        setExt(record?.assetId?.image?.format);
      }
    }
    getExtenstion();
  }, [record]);

  if (ext && ext === "image" && record?.assetId?.image?.compressed)
    return (
      <img
        src={record.assetId.image.compressed}
        alt={record?.assetId?.assetName || "Asset image"}
        className="w-[60px] h-[60px] object-contain rounded-[5px] m-2.5"
      />
    );
  if (ext && ext === "audio")
    return (
      <img
        src={AudioSquareCover}
        className="w-[60px] h-[60px] rounded-[5px] m-2.5"
        alt="Audio file"
      />
    );
  if (ext && ext === "video")
    return (
      <img
        src={VideoSquareCover}
        className="w-[60px] h-[60px] rounded-[5px] m-2.5"
        alt="Video file"
      />
    );
  return (
    <div>
      <CustomLoader size="lg" />
    </div>
  );
};

const RentalList = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [data, setData] = useState<NFTRecord[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showPopUp, setShowPopup] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [web3Data, setWeb3Data] = useState<Web3Data | null>(null);
  const [rentalContractInstance, setRentalContractInstance] =
    useState<any>(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState<{
    [key: string]: string;
  }>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<{
    assetAddress: string;
    listingId: number;
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/rental/getRentalList?page=${page}&limit=${perPage}`,
        );
        const result = await response.json();
        setData(result.data);

        setTotalPages(result.totalPages);
        setTotalRecords(result.totalRecords);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, perPage]);

  useEffect(() => {
    const initializeWeb3 = async () => {
      try {
        const web3Data = await services.getWeb3();
        if (web3Data) {
          setWeb3Data(web3Data as Web3Data);
        }
      } catch (error) {}
    };

    const initializeContract = async () => {
      try {
        const instance = await actions.getRentalPoolContract();
        setRentalContractInstance(instance);
      } catch (error) {}
    };

    const initializeTokenContract = async () => {
      const tokenInstance = await actions.getTokenContract();
      setTokenContract(tokenInstance);
    };

    initializeWeb3();
    initializeContract();
    initializeTokenContract();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/rentals/${id}`, {
        method: "DELETE",
      });
      setData(data.filter((item) => item.id !== id));
      setShowPopup(false);
      setDeleteId(null);
    } catch (error) {}
  };

  const handlePayRent = async (
    assetAddress: string,
    listingId: number,
    totalRentalPrice: number,
  ) => {
    try {
      const { rentalPool } = getContractAddresses();

      const owner = await rentalContractInstance.methods.owner().call();

      if (owner !== web3Data.accounts[0]) {
        toast({
          description: "You haven't logged in with the owner account.",

          variant: "destructive",
        });

        return;
      }

      const amountInWei = web3.utils.toWei(
        (totalRentalPrice || 0).toString(),
        "ether",
      );

      const getAllowance = await tokenContract.methods
        .allowance(web3Data.accounts[0], rentalPool)
        .call();

      if (
        Number(getAllowance) === 0 ||
        Number(getAllowance) < Number(amountInWei)
      ) {
        const maxUint256 =
          "115792089237316195423570985008687907853269984665640564039457584007913129639935";
        // Allowance call of  Utility token
        await tokenContract.methods
          .approve(rentalPool, maxUint256)
          .send({ from: web3Data.accounts[0] })
          .on("transactionHash", () => {
            toast({
              description: "Approving tokens...",
              variant: "default",
            });
          })
          .on("receipt", () => {
            toast({
              description: "Token approval successful",
              variant: "default",
            });
          })
          .on("error", (error: Web3Error) => {
            toast({
              description: "Token approval failed",
              variant: "destructive",
            });
            throw error;
          });
      }

      await rentalContractInstance.methods
        .payRent(assetAddress, listingId)
        .send({ from: web3Data?.accounts[0] })
        .on("transactionHash", (hash: any) => {
          sendTransactionHash(hash).catch((apiError) => {});
        })
        .on("receipt", (receipt: any) => {
          toast({
            description: "Rent payment successful!",
            variant: "default",
          });
          // Optionally, refresh the data after successful payment
          setPage(1);
          setPerPage(10);
        })
        .on("error", (error: any) => {
          toast({
            description: "Error paying rent. Please try again.",
            variant: "destructive",
          });
        });
    } catch (error) {
      toast({
        description: "Admin denied the transaction.",

        variant: "destructive",
      });
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedRecord) return;

    try {
      const owner = await rentalContractInstance.methods.owner().call();

      // if (owner !== web3Data?.accounts[0]) {
      //   toast({
      //     description: "You haven't logged in with the owner account.",
      //     variant: 'destructive',
      //   });
      //   return;
      // }

      const statusKey = `${selectedRecord.assetAddress}-${selectedRecord.listingId}`;
      if (selectedStatus[statusKey] === "REJECTED") {
        await rentalContractInstance.methods
          .rejectRentalListing(
            selectedRecord.assetAddress,
            selectedRecord.listingId,
          )
          .send({ from: web3Data?.accounts[0] })
          .on("transactionHash", (hash: any) => {
            sendTransactionHash(hash).catch((apiError) => {});
            toast({
              description: "Updating rental status...",
              variant: "default",
            });
          })
          .on("receipt", (receipt: any) => {
            toast({
              description: "Rental status updated successfully!",
              variant: "default",
            });
            setIsDialogOpen(false);
            setSelectedRecord(null);
            setSelectedStatus({});
            // Refresh the data
            setPage(1);
            setPerPage(10);
          })
          .on("error", (error: any) => {
            toast({
              description: "Error updating rental status. Please try again.",
              variant: "destructive",
            });
          });
      } else {
        toast({
          description: "This status update is not yet implemented.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        description: "Error updating rental status. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <CustomLoader size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4  p-6 rounded-lg">
      <h2 className="text-2xl text-black font-bold">List of Rental Property</h2>

      {showPopUp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Delete Rental-{name}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete this rental?
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPopup(false);
                  setDeleteId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteId && handleDelete(deleteId)}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px] text-center">
                Asset Name
              </TableHead>
              <TableHead className="text-center">Rental Price</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">View</TableHead>
              <TableHead className="text-center">End Date</TableHead>
              <TableHead className="text-center">Allow PayRent</TableHead>
              <TableHead className="text-center">Pay Rent</TableHead>
              <TableHead className="text-center">
                Update Rental Status
              </TableHead>
              <TableHead className="text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center">
                  No rentals yet!
                </TableCell>
              </TableRow>
            ) : (
              data.map((record) => (
                <TableRow
                  key={record.id}
                  className="group cursor-pointer transition-colors"
                >
                  <TableCell className="max-w-[200px] truncate font-medium group-hover:bg-muted text-center">
                    {record?.assetId?.assetName}
                  </TableCell>
                  <TableCell className="text-center group-hover:bg-muted">
                    {record.totalRentalPrice}
                  </TableCell>
                  <TableCell className="text-center group-hover:bg-muted">
                    <RentStatusField record={record} />
                  </TableCell>
                  <TableCell className="text-center group-hover:bg-muted">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => navigate(`/rental/${record._id}`)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                  <TableCell className="text-center group-hover:bg-muted">
                    {new Date(record?.assetId?.saleEndDate).toLocaleDateString(
                      "en-GB",
                      {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      },
                    )}
                  </TableCell>
                  <TableCell className="text-center group-hover:bg-muted">
                    {record.allowPayRent ? "Tenant" : "Admin"}
                  </TableCell>
                  <TableCell className="text-center group-hover:bg-muted">
                    <Button
                      className="bg-background border border-surface-2 text-surface-2 dark:text-surface-2 dark:bg-surface-2 dark:border-surface-2 hover:bg-secondary dark:hover:bg-secondary"
                      onClick={() =>
                        handlePayRent(
                          record?.assetAddress,
                          Number(record?.listingId),
                          Number(record?.totalRentalPrice),
                        )
                      }
                      disabled={record?.allowPayRent || record?.rentPaid}
                    >
                      Pay Rent
                    </Button>
                  </TableCell>
                  <TableCell className="text-center group-hover:bg-muted">
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          onClick={() =>
                            setSelectedRecord({
                              assetAddress: record.assetAddress,
                              listingId: Number(record.listingId),
                            })
                          }
                        >
                          Update Status
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Update Rental Status</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col gap-4 py-4">
                          <Select
                            value={
                              selectedRecord
                                ? selectedStatus[
                                    `${selectedRecord.assetAddress}-${selectedRecord.listingId}`
                                  ] || ""
                                : ""
                            }
                            onValueChange={(value) => {
                              if (selectedRecord) {
                                setSelectedStatus({
                                  ...selectedStatus,
                                  [`${selectedRecord.assetAddress}-${selectedRecord.listingId}`]:
                                    value,
                                });
                              }
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PENDING">PENDING</SelectItem>
                              <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                              <SelectItem value="COMPLETED">
                                COMPLETED
                              </SelectItem>
                              <SelectItem value="REJECTED">REJECTED</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            className="bg-gradientButton"
                            onClick={handleStatusUpdate}
                            disabled={
                              !selectedRecord ||
                              !selectedStatus[
                                `${selectedRecord.assetAddress}-${selectedRecord.listingId}`
                              ]
                            }
                          >
                            Confirm Update
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                  <TableCell className="text-center group-hover:bg-muted">
                    <Button
                      variant="destructive"
                      size="icon"
                      className="bg-background border border-surface-2 dark:bg-surface-2 dark:border-surface-2 hover:bg-secondary dark:hover:bg-secondary"
                      onClick={() => {
                        setShowPopup(true);
                        setDeleteId(record._id);
                        setName(record?.assetId?.assetName);
                      }}
                    >
                      <Trash2 className="h-4 w-4" color="black" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between px-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          Showing {(page - 1) * perPage + 1} to{" "}
          {Math.min(page * perPage, totalRecords)} of {totalRecords} entries
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <select
              value={perPage}
              onChange={(e) => {
                setPage(1);
                setPerPage(Number(e.target.value));
              }}
              className="h-8 w-[70px] rounded-md border border-input bg-transparent px-2 py-1 text-sm"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="40">40</option>
              <option value="50">50</option>
            </select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {page} of {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              Last
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalList;
